import {
  useCallback,
  useEffect,
  useState,
  ReactElement,
  MouseEvent,
} from "react";

import {
  EmailAddressFieldConfig,
  ValidatableEmailAddressFieldConfig,
} from "@evergiving/common/dist/es2015/form";
import { SuccessfulKickboxAppraisal } from "@evergiving/common/dist/es2015/pledge";

import { resetFormErrors, setEmailAddress } from "@/lib/redux/actions";
import { LabelledField } from "@/lib/components/LabelledField";
import { LabelText } from "@/lib/components/LabelText";
import { InputFieldConfig } from "@/lib/misc/InputFieldConfig";
import { FieldError } from "@/lib/components/FieldError";
import { useAppDispatch, useAppSelector } from "@/lib/hooks/redux";
import {
  errorsForEmailAddress,
  validationContextSelector,
} from "@/lib/misc/validations";
import { Text } from "@/lib/components/Text";
import { useAutofill } from "@/lib/hooks/useAutofill";

function DidYouMean({
  config,
  suggestion,
}: {
  config: ValidatableEmailAddressFieldConfig;
  suggestion: string;
}) {
  const dispatch = useAppDispatch();

  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      dispatch(setEmailAddress(config, suggestion));
    },
    [config, dispatch, suggestion]
  );

  return (
    <Text
      value={config.validateAddress?.didYouMeanText}
      args={{
        email: (
          <button
            type="button"
            className="emailSuggestion"
            onClick={handleClick}
          >
            {suggestion}
          </button>
        ),
      }}
    />
  );
}

function warningsForAppraisal(
  config: ValidatableEmailAddressFieldConfig,
  appraisal: SuccessfulKickboxAppraisal
): (string | ReactElement)[] {
  if (appraisal.result === "risky" && appraisal.sendex < 0.2) {
    return [config.validateAddress.riskyWarningText];
  }
  if (appraisal.did_you_mean) {
    return [<DidYouMean config={config} suggestion={appraisal.did_you_mean} />];
  }
  return [];
}

export function EmailAddressField({
  config,
  autoFocus,
}: {
  config: EmailAddressFieldConfig;
} & InputFieldConfig): ReactElement {
  const name = "emailAddress";
  const required = !!config.requiredFieldErrorText;
  const valueFromStore = useAppSelector((state) => state[name].value);
  const appraisal = useAppSelector((state) => state.emailAppraisal);
  const [currentValue, setCurrentValue] = useState<string>(
    valueFromStore || ""
  );
  const dispatch = useAppDispatch();
  const validationContext = useAppSelector(validationContextSelector);
  const [isDirty, setIsDirty] = useState(false);
  const errorMessages = errorsForEmailAddress(
    config,
    valueFromStore,
    appraisal?.appraisedEmailAddress.appraisal,
    validationContext
  );
  const anyErrors = errorMessages.length > 0;

  const saveData = useCallback(
    async (value: string) => {
      const newValue = value.trim();

      dispatch(
        setEmailAddress(config, newValue.length > 0 ? newValue : undefined)
      );

      setIsDirty(true);
    },
    [dispatch, config]
  );

  const {
    detectAutofillByAnimation,
    handleBlurWithAutofill,
    handleFocusWithAutofill,
    handleInputWithAutofillSafariHack,
    saveDataWithAutofill,
  } = useAutofill({ saveData });

  const handleChange = useCallback(
    async ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(resetFormErrors(name));

      saveDataWithAutofill(value);

      setCurrentValue(value);
    },
    [dispatch, name, saveDataWithAutofill, setCurrentValue]
  );

  useEffect(() => {
    setCurrentValue(valueFromStore || "");
  }, [valueFromStore, setCurrentValue]);

  const errorId = `${name}-errors`;

  const warnings: (string | ReactElement)[] =
    !anyErrors &&
    appraisal?.appraisedEmailAddress.appraisal &&
    "validateAddress" in config
      ? warningsForAppraisal(config, appraisal.appraisedEmailAddress.appraisal)
      : [];

  return (
    <LabelledField id={name}>
      <LabelText requiredIndicator={required}>{config.labelText}</LabelText>
      {/* eslint-disable jsx-a11y/no-autofocus */}
      {/* ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^ Discussion of this override follows:

       We're only passing through the attribute here.  See DonationDetailsPage for
       a discussion why we would ever want to set this attribute.

       */}
      <input
        className="textField"
        type="text"
        name="email"
        placeholder={config.placeholderText}
        autoComplete="email"
        inputMode="email"
        onAnimationStart={detectAutofillByAnimation}
        onBlur={handleBlurWithAutofill}
        onChange={handleChange}
        onFocus={handleFocusWithAutofill}
        onInput={handleInputWithAutofillSafariHack}
        value={currentValue}
        autoFocus={autoFocus}
        aria-required={required}
        aria-invalid={(anyErrors && isDirty) || undefined}
        aria-describedby={anyErrors ? errorId : undefined}
      />

      {/* eslint-enable jsx-a11y/no-autofocus */}
      <div role="alert">
        {anyErrors && isDirty ? (
          <FieldError id={errorId} messages={errorMessages} />
        ) : null}
        {warnings && (
          <FieldError id={errorId} messages={warnings} className="warnings" />
        )}
      </div>
    </LabelledField>
  );
}
